import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Clock, Target, ArrowRight, Plus, Trash2 } from 'lucide-react';
import { firebaseService } from '../services/firebaseService';
import { auth } from '../services/firebase';

interface Roadmap {
  subject: string;
  slug: string;
  modules: any[];
  preferences: {
    level: string;
    goal: string;
    timeCommitment: string;
  };
  completedLessons: { [key: string]: boolean };
}

interface DashboardViewProps {
  roadmaps: Roadmap[];
  onStartNewRoadmap: () => void;
  onContinueRoadmap: (roadmap: Roadmap) => void;
  onRoadmapDeleted?: (slug: string) => void;
  isGuestMode?: boolean;
}

export function DashboardView({ 
  roadmaps, 
  onStartNewRoadmap, 
  onContinueRoadmap, 
  onRoadmapDeleted,
  isGuestMode = false
}: DashboardViewProps) {
  const calculateProgress = (roadmap: Roadmap) => {
    const totalLessons = roadmap.modules.reduce((acc, module) => acc + module.lessons.length, 0);
    const completedCount = Object.values(roadmap.completedLessons || {}).filter(Boolean).length;
    return totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;
  };

  const handleDeleteRoadmap = async (roadmap: Roadmap) => {
    if (window.confirm(`Are you sure you want to delete "${roadmap.subject}"? This action cannot be undone.`)) {
      try {
        if (isGuestMode) {
          // For guest mode, we'll handle deletion through the parent component
          onRoadmapDeleted?.(roadmap.slug);
        } else {
          const user = auth.currentUser;
          if (!user) {
            throw new Error('User not authenticated');
          }
          
          await firebaseService.deleteRoadmap(user.uid, roadmap.slug);
          onRoadmapDeleted?.(roadmap.slug);
        }
      } catch (error) {
        console.error('Error deleting roadmap:', error);
        alert('Failed to delete roadmap. Please try again.');
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Your Learning Journey</h1>
          {isGuestMode && (
            <p className="text-sm text-blue-600 mt-1">Guest Mode - Data stored locally in your browser</p>
          )}
        </div>
        <button
          onClick={onStartNewRoadmap}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <Plus className="w-5 h-5" />
          <span>New Roadmap</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roadmaps.map((roadmap, index) => (
          <motion.div
            key={roadmap.slug}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{roadmap.subject}</h3>
                  <p className="text-sm text-gray-500">{roadmap.preferences.level}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleDeleteRoadmap(roadmap)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    title="Delete roadmap"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Target className="w-4 h-4" />
                  <span className="line-clamp-1">{roadmap.preferences.goal}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{roadmap.preferences.timeCommitment}</span>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Progress</span>
                  <span className="font-medium text-blue-600">{calculateProgress(roadmap)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-300"
                    style={{ width: `${calculateProgress(roadmap)}%` }}
                  />
                </div>
              </div>

              <button
                onClick={() => onContinueRoadmap(roadmap)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <span>Continue Learning</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}

        {roadmaps.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No roadmaps yet</h3>
            <p className="text-gray-600 mb-6">Start your learning journey by creating your first roadmap</p>
            <button
              onClick={onStartNewRoadmap}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Plus className="w-5 h-5" />
              <span>Create New Roadmap</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}